/** Formato de números, monedas y fechas para la UI (es-AR). La API usa ISO y montos como string. */
export type Moneda = "ARS" | "USD";

const numero = new Intl.NumberFormat("es-AR", { minimumFractionDigits: 0, maximumFractionDigits: 2 });
const monedas: Record<Moneda, Intl.NumberFormat> = {
  ARS: new Intl.NumberFormat("es-AR", { style: "currency", currency: "ARS", minimumFractionDigits: 2 }),
  USD: new Intl.NumberFormat("es-AR", { style: "currency", currency: "USD", currencyDisplay: "code", minimumFractionDigits: 2 }),
};
const porcentaje = new Intl.NumberFormat("es-AR", { maximumFractionDigits: 1 });

function aNumero(valor: string | number): number {
  return typeof valor === "number" ? valor : Number(valor);
}

export function formatNumero(valor: string | number): string {
  return numero.format(aNumero(valor));
}

/** formatMoneda("1450.5", "ARS") → "$ 1.450,50"; en USD → "USD 1.450,50". Sólo para mostrar: no hacer cuentas con el resultado. */
export function formatMoneda(valor: string | number, moneda: Moneda): string {
  return monedas[moneda].format(aNumero(valor)).replace(/\u00a0/g, " ");
}

/** formatPorcentaje(0.425) → "42,5 %" (armado a mano: el espacio varía entre motores de ICU) */
export function formatPorcentaje(fraccion: number): string {
  return `${porcentaje.format(fraccion * 100)} %`;
}

/** "2026-09-24" o ISO con hora → "24/09/2026". Sin pasar por Date para no correr el día por zona horaria. */
export function formatFecha(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : "";
}

/** Timestamp ISO con hora (ej. `creado_en`, en UTC) → "dd/mm/aaaa" en la zona horaria local.
 * A diferencia de `formatFecha` (que no pasa por `Date` para no correr fechas *sin hora*), acá sí hace falta:
 * un timestamp guarda un instante, y mostrarlo como "el día" depende de la zona del que mira la pantalla
 * (en Argentina, un registro de las 21:00 o más tarde ya es "mañana" en UTC). No usar con fechas sin hora. */
export function formatFechaLocal(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const dia = String(d.getDate()).padStart(2, "0");
  const mes = String(d.getMonth() + 1).padStart(2, "0");
  return `${dia}/${mes}/${d.getFullYear()}`;
}

/** Fecha de hoy en la zona horaria local (no UTC): en Argentina (UTC−3), desde las 21:00 `toISOString()` ya da mañana. */
export function hoyISO(d: Date = new Date()): string {
  const mes = String(d.getMonth() + 1).padStart(2, "0");
  const dia = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mes}-${dia}`;
}

/** "24/09/2026" → "2026-09-24" (o null si no es válida). */
export function parseFecha(ddmmaaaa: string): string | null {
  const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(ddmmaaaa.trim());
  if (!m) return null;
  const [, d, mes, a] = m as unknown as [string, string, string, string];
  const fecha = new Date(Date.UTC(Number(a), Number(mes) - 1, Number(d)));
  if (fecha.getUTCDate() !== Number(d) || fecha.getUTCMonth() !== Number(mes) - 1) return null;
  return `${a}-${mes.padStart(2, "0")}-${d.padStart(2, "0")}`;
}
